<script setup lang="ts">
import Placeholder from '@tiptap/extension-placeholder'
import StarterKit from '@tiptap/starter-kit'
import { EditorContent, useEditor } from '@tiptap/vue-3'
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'
import { isRichTextEmpty, richTextToPlain, toRichText } from '../richText'

// Rich text field (Tiptap, MIT), same look as UiTextarea. v-model is HTML limited to the tags of
// richText.ts; an empty editor gives ''. Old plain text opens as paragraphs.
const props = withDefaults(
  defineProps<{
    label: string
    placeholder?: string
    hint?: string
    error?: string
    // Minimum height in lines, like the rows of a textarea.
    rows?: number
    // Limit of the text (without the formatting), shown as a counter.
    maxlength?: number
    disabled?: boolean
    id?: string
  }>(),
  { rows: 4 },
)

const model = defineModel<string>({ default: '' })

const generatedId = useId()
const inputId = computed(() => props.id ?? `richtext-${generatedId}`)
const labelId = computed(() => `${inputId.value}-label`)
const describedBy = computed(
  () =>
    [props.error ? `${inputId.value}-error` : null, props.hint ? `${inputId.value}-hint` : null].filter(Boolean).join(' ') ||
    undefined,
)

const toHtml = (editorHtml: string) => (isRichTextEmpty(editorHtml) ? '' : editorHtml)

const editor = useEditor({
  content: toRichText(model.value),
  editable: !props.disabled,
  extensions: [
    StarterKit.configure({
      heading: { levels: [3] },
      code: false,
      codeBlock: false,
      horizontalRule: false,
      link: { openOnClick: false, autolink: true, defaultProtocol: 'https', protocols: ['mailto', 'tel'] },
    }),
    Placeholder.configure({ placeholder: () => props.placeholder ?? '' }),
  ],
  editorProps: {
    attributes: () => ({
      id: inputId.value,
      role: 'textbox',
      'aria-multiline': 'true',
      'aria-labelledby': labelId.value,
      ...(describedBy.value ? { 'aria-describedby': describedBy.value } : {}),
      ...(props.error ? { 'aria-invalid': 'true' } : {}),
      class: 'ui-richtext__content',
    }),
  },
  onUpdate: ({ editor: current }) => {
    model.value = toHtml(current.getHTML())
  },
})

// Outside changes (form reset, value loaded later) reach the editor; its own echo doesn't.
watch(model, (value) => {
  const current = editor.value
  if (!current || toHtml(current.getHTML()) === (value ?? '')) return
  current.commands.setContent(toRichText(value), { emitUpdate: false })
})
watch(
  () => props.disabled,
  (disabled) => editor.value?.setEditable(!disabled),
)

const length = computed(() => richTextToPlain(model.value).length)

// Link: a small field under the toolbar instead of a browser prompt.
const linkOpen = ref(false)
const linkUrl = ref('')
const linkInput = ref<HTMLInputElement>()
async function openLink() {
  linkUrl.value = (editor.value?.getAttributes('link').href as string | undefined) ?? ''
  linkOpen.value = true
  await nextTick()
  linkInput.value?.focus()
}
function applyLink() {
  const href = linkUrl.value.trim()
  const chain = editor.value?.chain().focus().extendMarkRange('link')
  if (!href) chain?.unsetLink().run()
  else chain?.setLink({ href: /^[a-z][a-z0-9+.-]*:/i.test(href) ? href : `https://${href}` }).run()
  linkOpen.value = false
}
function cancelLink() {
  linkOpen.value = false
  editor.value?.commands.focus()
}

type Action = { key: string; label: string; glyph: string; active?: () => boolean; run: () => void; disabled?: () => boolean }
const chain = () => editor.value!.chain().focus()
const actions = computed<Action[][]>(() => [
  [
    { key: 'bold', label: 'Negrito', glyph: 'B', active: () => !!editor.value?.isActive('bold'), run: () => chain().toggleBold().run() },
    { key: 'italic', label: 'Itálico', glyph: 'I', active: () => !!editor.value?.isActive('italic'), run: () => chain().toggleItalic().run() },
    { key: 'underline', label: 'Sublinhado', glyph: 'U', active: () => !!editor.value?.isActive('underline'), run: () => chain().toggleUnderline().run() },
    { key: 'strike', label: 'Tachado', glyph: 'S', active: () => !!editor.value?.isActive('strike'), run: () => chain().toggleStrike().run() },
  ],
  [
    { key: 'heading', label: 'Título', glyph: 'T', active: () => !!editor.value?.isActive('heading'), run: () => chain().toggleHeading({ level: 3 }).run() },
    { key: 'bullet', label: 'Lista com marcadores', glyph: '•', active: () => !!editor.value?.isActive('bulletList'), run: () => chain().toggleBulletList().run() },
    { key: 'ordered', label: 'Lista numerada', glyph: '1.', active: () => !!editor.value?.isActive('orderedList'), run: () => chain().toggleOrderedList().run() },
    { key: 'quote', label: 'Citação', glyph: '“', active: () => !!editor.value?.isActive('blockquote'), run: () => chain().toggleBlockquote().run() },
    { key: 'link', label: 'Link', glyph: '', active: () => !!editor.value?.isActive('link'), run: () => void openLink() },
  ],
  [
    { key: 'undo', label: 'Desfazer', glyph: '↶', run: () => chain().undo().run(), disabled: () => !editor.value?.can().undo() },
    { key: 'redo', label: 'Refazer', glyph: '↷', run: () => chain().redo().run(), disabled: () => !editor.value?.can().redo() },
  ],
])

onBeforeUnmount(() => editor.value?.destroy())
</script>

<template>
  <div class="ui-field">
    <span :id="labelId" class="ui-field__label" @click="editor?.commands.focus()">{{ label }}</span>
    <div
      class="ui-richtext"
      :class="{ 'ui-richtext--invalid': !!error, 'ui-richtext--disabled': disabled }"
      :style="{ '--ui-richtext-rows': rows }"
    >
      <div v-if="!disabled" class="ui-richtext__toolbar" role="toolbar" :aria-label="`Formatação de ${label}`">
        <template v-for="(group, index) in actions" :key="index">
          <span v-if="index" class="ui-richtext__separator" aria-hidden="true"></span>
          <button
            v-for="action in group"
            :key="action.key"
            type="button"
            class="ui-richtext__button"
            :class="[`ui-richtext__button--${action.key}`, { 'ui-richtext__button--active': action.active?.() }]"
            :aria-label="action.label"
            :title="action.label"
            :aria-pressed="action.active ? action.active() : undefined"
            :disabled="action.disabled?.()"
            @mousedown.prevent
            @click="action.run()"
          >
            <svg
              v-if="action.key === 'link'"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            <template v-else>{{ action.glyph }}</template>
          </button>
        </template>
      </div>
      <div v-if="linkOpen" class="ui-richtext__link">
        <input
          ref="linkInput"
          v-model="linkUrl"
          type="url"
          class="ui-richtext__link-input"
          placeholder="https://"
          aria-label="Endereço do link (vazio remove)"
          @keydown.enter.prevent="applyLink"
          @keydown.esc.prevent="cancelLink"
        />
        <button type="button" class="ui-richtext__link-button" @click="applyLink">Aplicar</button>
        <button type="button" class="ui-richtext__link-button" @click="cancelLink">Cancelar</button>
      </div>
      <EditorContent :editor="editor" />
    </div>
    <span v-if="error" :id="`${inputId}-error`" role="alert" class="ui-field__error">{{ error }}</span>
    <span v-if="hint || maxlength" :id="hint ? `${inputId}-hint` : undefined" class="ui-field__hint ui-richtext__hint">
      <span>{{ hint }}</span>
      <span v-if="maxlength" :class="{ 'ui-richtext__count--over': length > maxlength }">{{ length }}/{{ maxlength }}</span>
    </span>
  </div>
</template>

<style scoped>
.ui-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.ui-field__label {
  font-size: var(--type-body-size);
  font-weight: var(--weight-medium);
  color: var(--text-label);
}
.ui-field__error {
  font-size: var(--type-footnote-size);
  color: var(--error);
}
.ui-field__hint {
  font-size: var(--type-footnote-size);
  color: var(--text-secondary);
}
.ui-richtext__hint {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}
.ui-richtext__count--over {
  color: var(--error);
}
.ui-richtext {
  border: 1px solid var(--opaque-separator);
  border-radius: var(--radius-field);
  background: var(--surface-card);
  min-width: 0;
}
.ui-richtext:focus-within {
  border-color: var(--accent);
}
.ui-richtext--invalid {
  border-color: var(--error);
}
.ui-richtext--disabled {
  opacity: 0.6;
}
.ui-richtext__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 4px 6px;
  border-bottom: var(--hairline) solid var(--separator-soft);
  background: var(--surface-subtle);
  border-radius: var(--radius-field) var(--radius-field) 0 0;
}
.ui-richtext__separator {
  width: 1px;
  height: 16px;
  margin: 0 4px;
  background: var(--separator);
}
.ui-richtext__button {
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  border: 0;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--text-label);
  font: inherit;
  font-size: var(--type-body-size);
  cursor: pointer;
}
.ui-richtext__button :deep(svg) {
  width: 14px;
  height: 14px;
}
.ui-richtext__button:hover:not(:disabled) {
  background: var(--secondary-system-fill);
}
.ui-richtext__button:disabled {
  opacity: 0.35;
  cursor: default;
}
.ui-richtext__button--active {
  background: var(--system-fill);
  color: var(--text-primary);
}
.ui-richtext__button--bold {
  font-weight: 700;
}
.ui-richtext__button--italic {
  font-style: italic;
}
.ui-richtext__button--underline {
  text-decoration: underline;
}
.ui-richtext__button--strike {
  text-decoration: line-through;
}
.ui-richtext__link {
  display: flex;
  gap: 6px;
  padding: 6px;
  border-bottom: var(--hairline) solid var(--separator-soft);
}
.ui-richtext__link-input {
  flex: 1;
  min-width: 0;
  padding: 6px 8px;
  border: 1px solid var(--opaque-separator);
  border-radius: var(--radius-field);
  font: inherit;
  font-size: var(--type-body-size);
  color: var(--text-primary);
  background: var(--surface-card);
}
.ui-richtext__link-button {
  border: 0;
  background: none;
  color: var(--accent);
  font: inherit;
  font-size: var(--type-footnote-size);
  cursor: pointer;
}
:deep(.ui-richtext__content) {
  min-height: calc(var(--ui-richtext-rows) * 1.45em + 20px);
  padding: 8px 12px;
  font-size: var(--type-body-size);
  line-height: 1.5;
  color: var(--text-primary);
  outline: none;
  overflow-wrap: anywhere;
}
:deep(.ui-richtext__content > :first-child) {
  margin-top: 0;
}
:deep(.ui-richtext__content > :last-child) {
  margin-bottom: 0;
}
:deep(.ui-richtext__content p) {
  margin: 0 0 0.6em;
}
:deep(.ui-richtext__content h3) {
  margin: 0.8em 0 0.4em;
  font-size: var(--type-headline-size, 1.05em);
}
:deep(.ui-richtext__content ul),
:deep(.ui-richtext__content ol) {
  margin: 0 0 0.6em;
  padding-left: 1.4em;
}
:deep(.ui-richtext__content blockquote) {
  margin: 0 0 0.6em;
  padding-left: 10px;
  border-left: 3px solid var(--separator);
  color: var(--text-secondary);
}
:deep(.ui-richtext__content a) {
  color: var(--accent);
}
:deep(.ui-richtext__content p.is-editor-empty:first-child::before) {
  content: attr(data-placeholder);
  float: left;
  height: 0;
  color: var(--text-placeholder);
  pointer-events: none;
}
</style>
